// 🔧 pet_show_playbook.js
// ✅ Purpose: Public-facing event playbook for running a pet show with Let's Go Vote
// ✅ Features:
// - Explains how pet contests can use categories, public entries, QR codes, voting, and winners
// - Public and SEO-friendly
// - Uses the Dashboard-style LGV layout
// - Helps prospective customers understand the full pet show workflow
// - Fully block-commented for clarity and future devs

import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../../components/seo_head";

function PetShowPlaybook() {
  return (
    <section id="top-content" className="container-fluid py-5">
      <SeoHead
        title="Pet Show Playbook | Let's Go Vote"
        description="Learn how to run a pet show or pet contest with public entries, fun categories, QR code voting, sponsors, and winners using Let's Go Vote."
        path="/guides/pet-show-playbook"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Guides", path: "/guides" },
          {
            name: "Pet Show Playbook",
            path: "/guides/pet-show-playbook",
          },
        ]}
      />
      <div className="row mb-4">
        <div className="col text-start">
          <h1 className="display-5">🐾 Pet Show Playbook</h1>
          <p className="lead text-muted">
            Learn how to run a pet contest with public entries, fun categories,
            QR voting, sponsors, and winners.
          </p>
        </div>
      </div>

      <div className="white-section p-4 rounded shadow-sm bg-white">
        <section className="mb-5">
          <div className="alert alert-info text-start">
            <h5 className="mb-3">Pet contests should be fun and easy</h5>
            <p className="mb-0">
              Let&apos;s Go Vote helps pet show organizers collect entries,
              organize categories, share QR codes, collect votes, and publish
              winners without paper ballots.
            </p>
          </div>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 1: Create your pet show event</h3>
          <p>
            Add your event name, dates, description, logo, and event images.
            Your public event page gives pet owners and attendees one place to
            find contest details, entries, sponsors, and results.
          </p>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 2: Choose your voting style</h3>
          <p>
            Most pet contests work well with People&apos;s Choice Voting, where
            attendees vote for their favorite pets in each category. Some events
            may also use judged scoring for formal competitions.
          </p>

          <Link
            to="/guides/configure-voting"
            className="btn btn-sm btn-outline-primary"
          >
            Read the Voting Guide
          </Link>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 3: Create fun pet categories</h3>
          <p>
            Pet show categories can be playful and easy to understand. Examples
            include Cutest Pet, Best Costume, Best Trick, Best Smile, Best
            Rescue Story, Most Unique, and Best in Show.
          </p>

          <div className="alert alert-secondary text-start">
            <strong>Tip:</strong> Fun categories encourage more participation
            and make voting easier for families and attendees.
          </div>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 4: Accept pet entries</h3>
          <p>
            Public entry submissions can allow pet owners to submit pet names,
            photos, descriptions, and social links before the event. Managers
            can review and approve entries before they appear publicly.
          </p>

          <Link
            to="/guides/categories-and-entries"
            className="btn btn-sm btn-outline-primary"
          >
            Read Categories & Entries
          </Link>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 5: Use QR codes for voting</h3>
          <p>
            QR codes can send attendees directly to an entry or voting page.
            This makes it simple for people to vote from their phones while
            walking through the event.
          </p>

          <Link to="/guides/qr-codes" className="btn btn-sm btn-outline-primary">
            Read the QR Code Guide
          </Link>
        </section>

        <section className="mb-5">
          <h3 className="mb-3">Step 6: Announce winners</h3>
          <p>
            After voting closes, managers can review results and display winners
            publicly. Winners can be shown by category, Best in Show, or special
            sponsor awards.
          </p>
        </section>

        <section className="mb-0">
          <div className="alert alert-primary text-start mb-0">
            <h5 className="mb-3">Planning a pet show?</h5>
            <p className="mb-3">
              Let&apos;s Go Vote can help your event manage entries, QR code
              voting, fun categories, sponsors, and winner displays.
            </p>

            <Link to="/request_event" className="btn btn-primary">
              Request an Event
            </Link>
          </div>
        </section>
      </div>
    </section>
  );
}

export default PetShowPlaybook;